import { useEffect, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { trackFunnelEvent, getAttribution } from '@/utils/tracking';

/**
 * StickyMobileCta — mobiele sticky CTA onderaan de landing.
 *
 * P0 Flow-fix (Feb 2026):
 *   De sticky CTA gaat NIET meer naar `/energy-control-intake`. Klik gaat
 *   direct naar Stripe Checkout (`/api/subscriptions/monitoring/checkout`).
 *   Setup-vragen pas in `/app` na aankoop.
 *
 * Safety:
 *   - Mobile only (hidden on >=md).
 *   - Hidden until visitor scrolled past hero.
 *   - Hidden on intake/checkout/admin/demo routes.
 */
export default function StickyMobileCta() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let trackedImpression = false;
    const onScroll = () => {
      const shouldShow = window.scrollY > 400;
      setShow(shouldShow);
      if (shouldShow && !trackedImpression) {
        trackedImpression = true;
        try {
          trackFunnelEvent('sticky_cta_impression', {
            meta: { surface: 'homepage_hero', viewport_w: window.innerWidth },
          });
        } catch (e) { /* ignore */ }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goToCheckout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      try {
        trackFunnelEvent('homepage_primary_cta_clicked', {
          meta: { cta_id: 'sticky_mobile_bottom', label: 'Start Mijn Powersure' },
        });
        trackFunnelEvent('sticky_cta_clicked', { meta: { surface: 'homepage_hero' } });
        trackFunnelEvent('payment_clicked', { plan: 'monitoring_monthly_intro' });
      } catch (e) { /* ignore */ }

      const attr = getAttribution() || {};
      const sessionId =
        (typeof window !== 'undefined' && sessionStorage.getItem('powersure_session')) || null;
      const res = await fetch('/api/subscriptions/monitoring/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: null,
          lead_id: null,
          intake_id: null,
          ref_code: null,
          session_id: sessionId,
          gclid: attr.gclid || null,
          gbraid: attr.gbraid || null,
          wbraid: attr.wbraid || null,
          utm_source: attr.utm_source || null,
          utm_medium: attr.utm_medium || null,
          utm_campaign: attr.utm_campaign || null,
          utm_term: attr.utm_term || null,
          utm_content: attr.utm_content || null,
          landing_url: attr.landing_url || null,
          referrer: attr.referrer || null,
        }),
      });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (_err) { /* ignore */ }
    setLoading(false);
  };

  if (typeof window !== 'undefined') {
    const p = window.location.pathname;
    if (
      p.startsWith('/energy-control-intake') ||
      p.startsWith('/checkout') ||
      p.startsWith('/admin') ||
      p.startsWith('/demo') ||
      p.startsWith('/example-report') ||
      p.startsWith('/privacy') ||
      p.startsWith('/terms') ||
      p.startsWith('/app')
    ) {
      return null;
    }
  }

  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pt-2 transition-transform duration-300 ${
        show ? 'translate-y-0' : 'translate-y-full'
      }`}
      aria-hidden={!show}
      data-testid="sticky-mobile-cta"
    >
      <div className="bg-[#0e1b16]/95 backdrop-blur-md border border-emerald-400/30 shadow-2xl rounded-xl p-2.5 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-white/65 leading-tight">
            € 9,95/m · eerste 3 mnd · maandelijks opzegbaar
          </div>
          <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Start Mijn Powersure
          </div>
        </div>
        <button
          onClick={goToCheckout}
          disabled={loading}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 px-4 py-3 rounded-lg font-semibold text-sm flex items-center gap-1 shadow-md disabled:opacity-60"
          data-testid="sticky-mobile-cta-btn"
          aria-label="Start Mijn Powersure"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Start'}
          {!loading && <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
